// Finds durations in step text so cook mode can offer one-tap timers.
// English + German: "bake 25 min", "1 hour", "1½ hours", "10-12 minutes", "1–2 Std.", "30 Sek."
// Ranges use the upper bound (safer to check early than to burn).

export type DetectedTimer = { label: string; seconds: number };

const NUMBER = String.raw`(\d+(?:[.,]\d+)?\s*[½¼¾]?|[½¼¾])`;
const UNIT =
  String.raw`(hours?|hrs?\.?|hr\.?|h|stunden?|std\.?|minutes?|minuten?|mins?\.?|min\.?|seconds?|secs?\.?|sec\.?|sekunden?|sek\.?)(?![\p{L}])`;
const PATTERN = new RegExp(String.raw`${NUMBER}(?:\s*(?:-|–|to|bis)\s*${NUMBER})?\s*${UNIT}`, 'giu');

const VULGAR: Record<string, number> = { '½': 0.5, '¼': 0.25, '¾': 0.75 };

function toNumber(text: string): number {
  const t = text.replace(/\s+/g, '');
  const tail = t.slice(-1);
  if (tail in VULGAR) {
    const head = t.slice(0, -1);
    return (head ? Number(head.replace(',', '.')) : 0) + VULGAR[tail];
  }
  return Number(t.replace(',', '.'));
}

function unitSeconds(unit: string): number {
  const u = unit.toLowerCase();
  if (/^(h|hr|hrs|hour|hours|std|stunde|stunden)/.test(u)) return 3600;
  if (/^(sec|secs|second|seconds|sek|sekunde|sekunden)/.test(u)) return 1;
  return 60;
}

export function detectTimers(text: string): DetectedTimer[] {
  const found: DetectedTimer[] = [];
  for (const m of text.matchAll(PATTERN)) {
    const upper = toNumber(m[2] ?? m[1]);
    const seconds = Math.round(upper * unitSeconds(m[3]));
    if (seconds > 0 && seconds <= 48 * 3600 && !found.some(f => f.seconds === seconds)) {
      found.push({ label: m[0].trim(), seconds });
    }
  }
  return found;
}

/** 90 → "1:30", 3725 → "1:02:05" */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.ceil(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(sec).padStart(2, '0')}`;
}
